/**
 * @file Network status component using Bootstrap 5.3
 * @version 1.0.0
 * @author Matthieu Chareton
 * @since 2025-07-08
 */

const { defineComponent, h, ref, onMounted, onUnmounted } = Vue;

export default defineComponent({
  name: 'NetworkStatus',

  props: {
    showWhenOnline: {
      type: Boolean,
      default: false,
    },
    position: {
      type: String,
      default: 'top',
      validator: value => ['top', 'bottom'].includes(value),
    },
    variant: {
      type: String,
      default: 'banner',
      validator: value => ['banner', 'badge', 'toast'].includes(value),
    },
    hideDelay: {
      type: Number,
      default: 3000,
    },
  },

  emits: ['online', 'offline'],

  setup(props, { emit }) {
    const isOnline = ref(navigator.onLine);
    const showOnlineMessage = ref(false);
    const hideTimeout = ref(null);

    const handleOnline = () => {
      isOnline.value = true;
      emit('online');

      if (props.showWhenOnline) {
        showOnlineMessage.value = true;

        if (hideTimeout.value) {
          clearTimeout(hideTimeout.value);
        }

        hideTimeout.value = setTimeout(() => {
          showOnlineMessage.value = false;
        }, props.hideDelay);
      }
    };

    const handleOffline = () => {
      isOnline.value = false;
      showOnlineMessage.value = false;
      emit('offline');

      if (hideTimeout.value) {
        clearTimeout(hideTimeout.value);
      }
    };

    onMounted(() => {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
    });

    onUnmounted(() => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      if (hideTimeout.value) {
        clearTimeout(hideTimeout.value);
      }
    });

    const renderBanner = () => {
      if (isOnline.value && !showOnlineMessage.value) {
        return null;
      }

      const isOffline = !isOnline.value;
      const alertClass = isOffline ? 'alert-warning' : 'alert-success';
      const icon = isOffline ? 'bi-wifi-off' : 'bi-wifi';
      const message = isOffline
        ? 'You are offline. Changes will be saved locally and synchronized when connection is restored.'
        : 'Connection restored. Synchronizing data...';

      return h(
        'div',
        {
          class: `alert ${alertClass} alert-dismissible fade show mb-0 ${props.position === 'bottom' ? 'border-top' : 'border-bottom'}`,
          role: 'alert',
          style: {
            borderRadius: 0,
            position: 'sticky',
            top: props.position === 'top' ? 0 : 'auto',
            bottom: props.position === 'bottom' ? 0 : 'auto',
            zIndex: 1030,
          },
        },
        [
          h('div', { class: 'd-flex align-items-center' }, [
            h('i', { class: `${icon} me-2` }),
            h('span', { class: 'flex-grow-1' }, message),
            !isOffline &&
              h('button', {
                type: 'button',
                class: 'btn-close',
                'aria-label': 'Close',
                onClick: () => (showOnlineMessage.value = false),
              }),
          ]),
        ]
      );
    };

    const renderBadge = () => {
      if (isOnline.value && !showOnlineMessage.value) {
        return null;
      }

      const isOffline = !isOnline.value;
      const badgeClass = isOffline ? 'bg-warning text-dark' : 'bg-success';
      const icon = isOffline ? 'bi-wifi-off' : 'bi-wifi';
      const text = isOffline ? 'Offline' : 'Online';

      return h(
        'span',
        {
          class: `badge ${badgeClass} d-flex align-items-center`,
        },
        [h('i', { class: `${icon} me-1` }), text]
      );
    };

    const renderToast = () => {
      if (isOnline.value && !showOnlineMessage.value) {
        return null;
      }

      const isOffline = !isOnline.value;
      const toastClass = isOffline ? 'text-bg-warning' : 'text-bg-success';
      const icon = isOffline ? 'bi-wifi-off' : 'bi-wifi';
      const title = isOffline ? 'Connection Lost' : 'Connection Restored';
      const message = isOffline
        ? 'Working offline. Changes will sync when reconnected.'
        : 'Back online. Synchronizing data...';

      return h(
        'div',
        {
          class: `toast show ${toastClass}`,
          role: 'alert',
          'aria-live': 'assertive',
          'aria-atomic': 'true',
        },
        [
          h('div', { class: 'toast-header' }, [
            h('i', { class: `${icon} me-2` }),
            h('strong', { class: 'me-auto' }, title),
            !isOffline &&
              h('button', {
                type: 'button',
                class: 'btn-close',
                'aria-label': 'Close',
                onClick: () => (showOnlineMessage.value = false),
              }),
          ]),
          h('div', { class: 'toast-body' }, message),
        ]
      );
    };

    return () => {
      const variants = {
        banner: renderBanner,
        badge: renderBadge,
        toast: renderToast,
      };

      return variants[props.variant]();
    };
  },
});
